import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { apiGet, apiPost } from "../utils/api";
import SocialPostsView from "./SocialPostsView";
import DOMPurify from 'dompurify';

// Collapsible Description Component
const CollapsibleDescription = ({ description, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const contentRef = useRef(null);
  const collapsedRef = useRef(null);

  useEffect(() => {
    // Check if content exceeds 3 lines when mounted
    if (contentRef.current && collapsedRef.current) {
      const fullHeight = contentRef.current.scrollHeight;
      const collapsedHeight = collapsedRef.current.scrollHeight;
      
      // If the full content is taller than the collapsed version, show toggle
      setShouldShowToggle(fullHeight > collapsedHeight);
    }
  }, [description]);

  const renderHTML = (html) => {
    if (!html) return { __html: '' };
    return { __html: DOMPurify.sanitize(html) };
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`collapsible-description ${className}`}>
      {/* Hidden full content for measurement */}
      <div
        ref={contentRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 'auto',
          width: '100%',
          pointerEvents: 'none'
        }}
        dangerouslySetInnerHTML={renderHTML(description)}
      />
      
      {/* Hidden collapsed content for measurement */}
      <div
        ref={collapsedRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          width: '100%',
          pointerEvents: 'none',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: '1.6'
        }}
        dangerouslySetInnerHTML={renderHTML(description)}
      />
      
      {/* Actual visible content */}
      <div
        className={`description-content ${isExpanded ? 'expanded' : 'collapsed'}`}
        style={{
          display: isExpanded ? 'block' : '-webkit-box',
          WebkitLineClamp: isExpanded ? 'none' : 3,
          WebkitBoxOrient: 'vertical',
          overflow: isExpanded ? 'visible' : 'hidden',
          lineHeight: '1.6',
          transition: 'all 0.3s ease'
        }}
        dangerouslySetInnerHTML={renderHTML(description)}
      />
      
      {/* Toggle button */}
      {shouldShowToggle && (
        <button
          onClick={toggleExpanded}
          className="description-toggle-btn"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginTop: '8px',
            padding: '4px 0',
            textDecoration: 'underline'
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const PROCESSING_STEPS = [
  { label: 'Sending audio for transcription', icon: '1' },
  { label: 'Transcribing episode audio', icon: '2' },
  { label: 'Generating social media posts', icon: '3' },
  { label: 'Finalizing content', icon: '4' },
];

const ProcessingIndicator = ({ elapsedSeconds }) => {
  // Step thresholds based on CloudWatch timing data:
  // ~3s: audio sent to AssemblyAI
  // ~53s: transcription webhook returns
  // ~58s: generate post lambdas start (run in parallel)
  // ~70-76s: all posts generated (LinkedIn is slowest at ~18s)
  let activeStep = 0;
  if (elapsedSeconds > 3) activeStep = 1;
  if (elapsedSeconds > 55) activeStep = 2;
  if (elapsedSeconds > 70) activeStep = 3;

  return (
    <div className="processing-indicator">
      <div className="processing-header">
        <div className="processing-spinner" />
        <span className="processing-title">Processing your episode...</span>
      </div>
      <p className="processing-subtitle">This typically takes about a minute. You can stay on this page and we'll update automatically.</p>
      <div className="processing-steps">
        {PROCESSING_STEPS.map((step, index) => (
          <div
            key={index}
            className={`processing-step ${
              index < activeStep ? 'step-complete' : index === activeStep ? 'step-active' : 'step-pending'
            }`}
          >
            <div className="step-icon">
              {index < activeStep ? '\u2713' : step.icon}
            </div>
            <span className="step-label">{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EpisodeView = () => {
  const { feedId, episodeId } = useParams();
  const [episode, setEpisode] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotaInfo] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [batchApprovalInfo, setBatchApprovalInfo] = useState(null);
  const [socialPostsKey, setSocialPostsKey] = useState(0);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (feedId && episodeId) {
      fetchEpisode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId, episodeId]);

  // Polling effect: check for transcription completion while processing
  useEffect(() => {
    if (isProcessing) {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await apiGet(`/getEpisode`, { feedId, episodeId });
          if (response.episode?.transcriptionText) {
            setEpisode(response.episode);
            setIsProcessing(false);
            setProcessingStartTime(null);
            setError(null);
            setSocialPostsKey(k => k + 1);
          }
        } catch (err) {
          // Silently continue polling on error
        }
      }, 15000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isProcessing, feedId, episodeId]);

  // Elapsed time counter
  useEffect(() => {
    if (processingStartTime) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - processingStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [processingStartTime]);

  const fetchEpisode = async () => {
    try {
      setLoading(true);
      setImageError(false);
      const response = await apiGet(`/getEpisode`, { feedId, episodeId });
      setEpisode(response.episode);
      setError(null);
    } catch (error) {
      console.error("Error fetching episode:", error);
      setError("Failed to load episode. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = () => {
    if (imageError || !episode.imageUrl) {
      return "/images/podcast-placeholder.png";
    }
    return episode.imageUrl;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleTranscription = async () => {
    if (!episode || !episode.audioUrl) {
      setError("Error: Missing audio URL");
      return;
    }

    try {
      setIsTranscribing(true);
      setError(null);

      const userId = sessionStorage.getItem('userId');
      const feedId_episodeId = `${feedId}#${episodeId}`;

      const response = await apiPost('/transcribe-episode', {
        userId,
        feedId_episodeId,
        audioUrl: episode.audioUrl,
        forceProcess: true
      });

      if (response.autoProcessingDisabled) {
        setError("Note: Automatic processing is disabled for this feed, but we're processing this episode as requested.");
      }

      // Kick off processing state with polling
      setIsTranscribing(false);
      setIsProcessing(true);
      setProcessingStartTime(Date.now());
      setElapsedSeconds(0);
    } catch (error) {
      console.error("Error transcribing episode:", error);
      setIsTranscribing(false);
      if (error.response && error.response.status === 402) {
        setError("You've reached your episode quota limit. Please visit Billing Settings to upgrade your plan.");
      } else {
        setError("Failed to start transcription. Please try again later.");
      }
    }
  };

  const handleBatchApprovalReady = (info) => {
    setBatchApprovalInfo(info);
  };

  if (loading) {
    return (
      <div className="episode-view-container" id="episodeViewContainer">
        <div className="loading-spinner" id="loadingSpinner">Loading episode...</div>
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="episode-view-container" id="episodeViewContainer">
        <div className="episode-alert-message" id="episodeAlertMessage">
          {error || "Episode not found or failed to load."}
        </div>
      </div>
    );
  }

  return (
    <div className="episode-view-container" id="episodeViewContainer">
      <h2 className="episode-view-title" id="episodeViewTitle">{episode.title}</h2>
      
      <div className="episode-main-content" id="episodeMainContent">
        {/* Left column: Thumbnail and Audio Player */}
        <div className="episode-left-column" id="episodeLeftColumn">
          <div className="episode-image-container" id="episodeImageContainer">
            <img 
              src={getImageUrl()}
              alt={episode.title || "Podcast episode"} 
              className="episode-image"
              id="episodeImage"
              onError={handleImageError}
            />
          </div>
          
          {/* Audio player now below thumbnail */}
          {episode.audioUrl && (
            <div className="episode-audio-section" id="episodeAudioSection">
              <h3 className="episode-audio-heading" id="episodeAudioHeading">Listen:</h3>
              <div className="episode-audio-player-container" id="episodeAudioPlayerContainer">
                <audio 
                  controls 
                  src={episode.audioUrl} 
                  className="episode-audio-player"
                  id="episodeAudioPlayer"
                  preload="metadata"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Right column: Episode details */}
        <div className="episode-details-container" id="episodeDetailsContainer">
          <p className="episode-publish-date" id="episodePublishDate">
            Published on: {new Date(episode.publishedAt).toLocaleDateString()}
          </p>
          
          {episode.description && (
            <div className="episode-description-section" id="episodeDescriptionSection">
              <h3 className="episode-description-heading" id="episodeDescriptionHeading">Description:</h3>
              <CollapsibleDescription 
                description={episode.description}
                className="episode-description-content podcast-description"
              />
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <div className="episode-alert-message" id="episodeAlertMessage">{error}</div>
      )}
      
      {quotaInfo && (
        <div className="episode-quota-info" id="episodeQuotaInfo">
          <p className="episode-quota-used">Episodes used this month: {quotaInfo.episodesProcessed}</p>
          <p className="episode-quota-remaining">Episodes remaining: {quotaInfo.remainingEpisodes}</p>
        </div>
      )}

      {!episode.transcriptionText && !isProcessing && (
        <button
          onClick={handleTranscription}
          disabled={isTranscribing}
          className={`episode-transcribe-btn ${isTranscribing ? 'episode-transcribe-btn-loading' : ''}`}
          id="episodeTranscribeBtn"
        >
          {isTranscribing ? "Smacking..." : "Smack It (Generate Transcription)"}
        </button>
      )}

      {isProcessing && (
        <ProcessingIndicator elapsedSeconds={elapsedSeconds} />
      )}
      
      <div className="episode-social-posts-section" id="episodeSocialPostsSection">
        <h3 className="social-posts-heading" id="socialPostsHeading">Social Media Posts</h3>
        
        {/* Approve All button moved here, below the heading */}
        {batchApprovalInfo?.hasPendingPosts && (
          <div className="batch-approval-container" style={{ marginBottom: 'var(--space-4)' }}>
            <button
              onClick={() => batchApprovalInfo.batchApprove()}
              disabled={batchApprovalInfo.isBatchApproving}
              className="batch-approve-btn"
              id="batchApproveBtn"
            >
              {batchApprovalInfo.isBatchApproving 
                ? `Approving ${batchApprovalInfo.pendingCount} posts...` 
                : `Approve All ${batchApprovalInfo.pendingCount} Posts`}
            </button>
          </div>
        )}
        
        <SocialPostsView
          key={socialPostsKey}
          podcastId={`${feedId}#${episodeId}`}
          onBatchApprovalReady={handleBatchApprovalReady}
        />
      </div>

      {episode.transcriptionText && (
        <div className="episode-transcription-section" id="episodeTranscriptionSection">
          <h3 className="episode-transcription-heading" id="episodeTranscriptionHeading">Transcription:</h3>
          <div className="episode-transcription-box" id="episodeTranscriptionBox">
            {episode.transcriptionText}
          </div>
        </div>
      )}
      
    </div>
  );
};

export default EpisodeView;